import type { ProductCategoryAssignment } from '@erp/shared';
import { Search, Star } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useProductCategories } from '@/features/products/api/use-product-categories';
import { cn } from '@/lib/utils';

interface ProductCategoryPickerProps {
  value: readonly { categoryId: string; isPrimary?: boolean | undefined }[];
  onChange: (value: ProductCategoryAssignment[]) => void;
  disabled?: boolean | undefined;
}

export function ProductCategoryPicker({
  value,
  onChange,
  disabled = false,
}: ProductCategoryPickerProps): JSX.Element {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const categories = useProductCategories();
  const normalized = useMemo<ProductCategoryAssignment[]>(
    () =>
      value.map((entry) => ({ categoryId: entry.categoryId, isPrimary: entry.isPrimary === true })),
    [value],
  );
  const selected = useMemo(
    () => new Map(normalized.map((entry) => [entry.categoryId, entry])),
    [normalized],
  );
  const visible = (categories.data ?? []).filter((category) =>
    query.trim().length === 0
      ? true
      : category.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()),
  );

  if (categories.isPending) return <Skeleton className="h-40 w-full" />;
  if (categories.isError) {
    return (
      <button
        type="button"
        className="text-sm text-destructive underline"
        onClick={() => void categories.refetch()}
      >
        {t('products.categories.loadError')} · {t('common.retry')}
      </button>
    );
  }

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('products.categories.search')}
          aria-label={t('products.categories.search')}
          className="pl-9"
        />
      </div>
      {visible.length === 0 ? (
        <p className="p-4 text-center text-sm text-muted-foreground">
          {t('products.categories.empty')}
        </p>
      ) : (
        <div
          className="max-h-56 space-y-1 overflow-y-auto"
          role="group"
          aria-label={t('products.categories.assignments')}
        >
          {visible.map((category) => {
            const assignment = selected.get(category.id);
            return (
              <div
                key={category.id}
                className="flex min-h-11 items-center gap-2 rounded-md px-2 hover:bg-muted/50"
                style={{ paddingLeft: `${String(category.depth * 16 + 8)}px` }}
              >
                <Checkbox
                  id={`category-${category.id}`}
                  checked={assignment !== undefined}
                  disabled={disabled}
                  onCheckedChange={(checked) => {
                    if (checked === true)
                      onChange([
                        ...normalized,
                        { categoryId: category.id, isPrimary: normalized.length === 0 },
                      ]);
                    else {
                      const remaining = normalized.filter(
                        (entry) => entry.categoryId !== category.id,
                      );
                      const first = remaining[0];
                      if (assignment?.isPrimary === true && first !== undefined) {
                        remaining[0] = { categoryId: first.categoryId, isPrimary: true };
                      }
                      onChange(remaining);
                    }
                  }}
                />
                <label
                  htmlFor={`category-${category.id}`}
                  className="min-w-0 flex-1 cursor-pointer truncate text-sm"
                >
                  {category.name}
                </label>
                {assignment !== undefined ? (
                  <button
                    type="button"
                    disabled={disabled}
                    aria-label={t('products.categories.makePrimary', { name: category.name })}
                    title={
                      assignment.isPrimary
                        ? t('products.categories.primary')
                        : t('products.categories.makePrimary', { name: category.name })
                    }
                    className={cn(
                      'flex h-11 w-11 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      assignment.isPrimary
                        ? 'text-primary'
                        : 'text-muted-foreground hover:bg-accent',
                    )}
                    onClick={() =>
                      onChange(
                        normalized.map((entry) => ({
                          ...entry,
                          isPrimary: entry.categoryId === category.id,
                        })),
                      )
                    }
                  >
                    <Star
                      className={cn('h-4 w-4', assignment.isPrimary && 'fill-current')}
                      aria-hidden="true"
                    />
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        {t('products.categories.selected', { count: value.length })}
      </p>
    </div>
  );
}
